<template>
		<div class="container projects">
			<header class="row projects__header">
				<h1 class="projects__heading col-xs-12 col-xl-4">{{ projectHeading }}</h1>
				<ul class="projects__filters col-xs-12 col-xl-8">
					<li class="filter__button filter__button--all" @click="filterProjects($event, 'all', highlightProjects)">{{allName}}</li>
					<li v-for="type in projectTypes" @click="filterProjects($event, type.value, highlightProjects)" class="filter__button" :class="`filter__button--${type.value}`">
						{{locale === 'en' ? type.title.en : type.title.jp}}
					</li>
				</ul>
			</header>
			<div class="projects__container row">
				<div v-for="project in projects"
						 :key="project.title.en"
						 class="project project--all"
						 :class="`project--${project.projectTypeList}`">
					<nuxt-link v-if="project.slug.current != null"
										 :to="localePath(`/projects/${project.slug.current}`)"
										 class="project__link">
						<img :src="$urlFor(project.projectHero.asset.url, 700)"
										 :alt="locale === 'en' ? project.projectHeroAlt.en : project.projectHeroAlt.jp"
										 class="project__image"
										 loading="lazy"
										 :height="project.projectHero.asset.metadata.dimensions.height"
										 :width="project.projectHero.asset.metadata.dimensions.width" />

						<div class="project__panel">
							<h4 v-if="project.isFeatured" class="project__featured">{{ locale === 'en' ? 'Featured' : '特徴' }}</h4>
							<div class="project__details">
								<h3 class="project__title" v-if="project.title.en !== null">{{ locale === 'en' ? project.title.en : project.title.jp }}</h3>
								<h5 class="project__type" v-if="project.projectType !== null">{{ locale === 'en' ? project.projectType.en : project.projectType.jp }}</h5>
							</div>
						</div>
					</nuxt-link>
				</div>
			</div>
		</div>
</template>

<script setup>
	import { useI18n, useLocalePath } from '#imports'
  import { groq } from "@nuxtjs/sanity/runtime/groq";
  import {useHead, useRoute} from "nuxt/app";
  import {computed, ref, onMounted} from 'vue'
	const { locale } = useI18n()
	const localePath = useLocalePath()
  import { gsap } from "gsap";
  const query = groq`{
    'projects': *[_type == 'projects']{
        title,
        metaTitle,
        metaDescription,
        projectTypes[]->{
        	title,
        	value
        },
        projects[]->{
          title,
          slug,
          isFeatured,
          projectHero{
            asset->{
              metadata{
                dimensions{
                  height,
                  width
                }
              },
              url
            }
          },
          projectHeroAlt,
          projectType,
          projectTypeList,
          description,
          location,
          date,
          images[]{
            alt,
            caption,
            image{
            asset->{
              metadata{
                dimensions{
                  height,
                  width
                }
              },
              url
            } }
          }
        }
    }
  }`;
  const sanity = useSanity()
  const { data } = await useAsyncData('page:projects', () => sanity.fetch(query))
	const projectsPage = computed(() => data.value?.projects?.[0] ?? {})
  const projects = computed(() => projectsPage.value.projects ?? [])
	const projectTypes = computed(() => projectsPage.value.projectTypes ?? [])
  const htmlLang = computed(() => locale.value === 'en' ? 'en-US' : 'ja-JA')
	const { $getSafeLocalizedField } = useNuxtApp()
	const processedTitle = computed(() => {
		if (projectsPage.value.metaTitle) {
			return locale.value === 'en' ? projectsPage.value.metaTitle.en : projectsPage.value.metaTitle.jp
		}
		return 'Projects'
	})
	//const fallbackDescription = computed(() => locale.value === 'en' ? 'Since 2012, TokyoDex has been a leader in transforming spaces through innovative art solutions.' : '2012年の創立以来, TokyoDexは革新的なアートソリューションを通じて空間を変革してきました。' )
	const fallbackDescription = 'Since 2012, TokyoDex has been a leader in transforming spaces through innovative art solutions.'
	const processedDescription = computed(() => {
		const rawDescription = projectsPage.value?.metaDescription
		return $getSafeLocalizedField(rawDescription, locale.value, fallbackDescription);
	});

	useHead({
		htmlAttrs: {
			lang: htmlLang
		},
			title: computed(() => `TokyoDex - ${processedTitle.value}`),
		viewport: 'width=device-width, initial-scale=1, maximum-scale=1',
		charset: 'utf-8',
		meta: [
			{ name: 'description', content: processedDescription.value }
		],
	})
	const route = useRoute();
  const queryFilter = ref(route.query.type ? route.query.type : null);
	const projectHeading = computed(() => locale.value === 'en' ? projectsPage.value?.title?.en : projectsPage.value?.title?.jp)
  const allName = computed(() => locale.value === 'en' ? 'All' : '全部');
  function filterProjects(event, type, callback) {
    const hiddenTargets = gsap.utils.toArray('.project');
    const activeButtons = gsap.utils.toArray('.filter__button');
    hiddenTargets.forEach(hiddenTarget => hiddenTarget.classList.add('inactive'));
    activeButtons.forEach(activeButton => activeButton.classList.remove('inactive'));
    callback(event, type);
  }
  function highlightProjects(event, type){
    const targets = gsap.utils.toArray(`.project--${type}`);
    const currentButton = document.querySelector(`.filter__button--${type}`);
    targets.forEach(target => target.classList.remove('inactive'));
    currentButton.classList.add('inactive');
  }
	onMounted(() => {
	    if(queryFilter.value !== null) {
	      filterProjects(null, queryFilter.value, highlightProjects);
	    }
	  })
</script>

<style lang="scss" scoped>
@use '@/assets/sass/variables.scss' as v;
.projects {
  &__header {
    align-items: center;
    justify-content: space-between;
    margin: 5rem 0 1rem 0;
    position: relative;
		@include v.respond-to('small') {
			margin: 8.5rem 0 1rem 0;
		}
  }
  &__heading {
		font-family: var(--font);
    font-size: v.$font-size-xxl;
		line-height: v.$font-size-xxl;
    padding: 0;
  }
}

  .projects__container {
		display: grid;
		grid-gap: 1rem;
		grid-template-columns: 100%;
		@include v.respond-to('small') {
			grid-template-columns: repeat(3, 1fr);
		}
    .project {
      margin: 0;
      padding: 0;
      position: relative;
      &__link {
        display: block;
        height: 20rem;
        max-height: 100%;
        max-width: 100%;
				overflow: hidden;
      }
      &__featured {
        background-color: v.$transparentBg--faint;
        color: v.$white;
        display: block;
				font-weight: bold;
        font-size: v.$font-size;
        left: 0;
        margin: 0 0 0 0;
        padding: 0.5rem 1rem;
        position: absolute;
        top: 0;
        transform-origin: top left;
        transition: 0.3s;
      }
      .project__image {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
			.project__panel {
				background-color: transparent;
				display: block;
				height: 100%;
				left: 0;
				position: absolute;
				top: 0;
				width: 100%;
			}
      .project__details {
        background-color: v.$transparentBg--strong;
        bottom: 0.5rem;
        box-sizing: border-box;
				color: v.$white;
        display: block;
        max-width: 90%;
        padding: 1rem;
        position: absolute;
        right: 0.5rem;
        text-align: right;
        transform-origin: bottom right;
        transition: 0.3s;
        .project__type {
					border-top: 1px solid v.$lightGrey;
          font-size: v.$font-size * 0.8;
          line-height: v.$font-size * 0.8;
					font-weight: 500;
          margin: 0.5rem 0 0 0;
          padding: 0.5rem 0 0 0;
        }
        .project__title {
					font-family: var(--font);
					font-weight: bold;
          font-size: v.$font-size-med;
          line-height: v.$font-size-med * 1.25;
          margin: 0 0 0 0;
          padding: 0 0 0 0;
        }
      }
      &.inactive {
        display: none;
        filter: saturate(0) contrast(1.75);
        transition: 0.3s;
      }
      &:hover {
				.project__panel {
					background-color: v.$teal;
					opacity: 0.85;
					transition: 0.3s;
				}
        .project__details, .project__featured {
          background-color: transparent;
          scale: 1.05;
          transition: 0.3s;
        }
				.project__image {
					transform: scale(1.05);
					transition: 0.3s;
				}
      }
    }
  }
  .projects__filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 0;
    padding: 0;
    @include v.respond-to('xlarge') {
      justify-content: flex-end;
    }
    .filter__button {
      @include  v.button;
      font-size: 0.9rem;
      margin: 0.5rem 0.5rem 0.5rem 0;
      padding: 0.5rem 0.75rem;
			@include v.respond-to('large') {
				font-size: v.$font-size;
			}
      @include v.respond-to('xlarge') {
        margin: 0.5rem 0 0.5rem 1rem;
      }
      &.inactive {
				background-color: v.$blueGrey;
				color: v.$canvas;
        cursor: text;
      }
    }
  }
</style>
